import Footer from "@/layouts/footer";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import FAQ from "./components/faq";
import NavPage from "@/layouts/nav-header";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  alpha,
  Fade,
  Grid,
  Chip,
} from "@mui/material";
import {
  Business,
  TrendingUp,
  Security,
  Support,
  CheckCircle,
  Star,
} from "@mui/icons-material";

function AboutUsPage() {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const theme = useTheme();

  const sections = [
    {
      title: t("about.section3_step1_title"),
      description: t("about.section3_step1_description"),
      icon: <Business color="primary" />,
    },
    {
      title: t("about.section3_step2_title"),
      description: t("about.section3_step2_description"),
      icon: <TrendingUp color="primary" />,
    },
    {
      title: t("about.section3_step3_title"),
      description: t("about.section3_step3_description"),
      icon: <Security color="primary" />,
    },
    {
      title: t("about.section3_step4_title"),
      description: t("about.section3_step4_description"),
      icon: <Support color="primary" />,
    },
  ];

  const features = [
    {
      title: "Ease of Use",
      description: "A flexible and easy-to-use interface for everyone.",
      icon: <CheckCircle color="primary" />,
    },
    {
      title: "Fair Competition",
      description: "Allows suppliers to offer the best prices to buyers.",
      icon: <Star color="primary" />,
    },
    {
      title: "Complete Management",
      description: "From request submission to supplier selection with ease.",
      icon: <Business color="primary" />,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "grey.50",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <NavPage isForm={0}>
        <Box sx={{ mt: { xs: 8, md: 12 }, mb: 6, px: 2 }}>
          <Fade in={true}>
            <Box sx={{ maxWidth: 1200, mx: "auto" }}>
              {/* Header Section */}
              <Box sx={{ textAlign: "center", mb: 8 }}>
                <Typography
                  variant="h2"
                  fontWeight={700}
                  color="text.primary"
                  gutterBottom
                >
                  {t("about.title")}
                </Typography>
                <Typography
                  variant="h6"
                  color="primary"
                  fontWeight={600}
                  gutterBottom
                >
                  {t("about.section1_title")}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ maxWidth: 800, mx: "auto" }}
                >
                  {t("about.section1_body")}
                </Typography>
              </Box>

              {/* Mission Section */}
              <Card
                elevation={2}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  mb: 6,
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Business color="primary" sx={{ fontSize: 32 }} />
                    <Typography
                      variant="h4"
                      fontWeight={600}
                      color="text.primary"
                    >
                      {t("about.section2_title")}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2, lineHeight: 1.8 }}
                  >
                    {t("about.section2_body")}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ lineHeight: 1.8 }}
                  >
                    {t("about.section2_additional")}
                  </Typography>
                </CardContent>
              </Card>

              {/* How It Works Section */}
              <Box sx={{ mb: 8 }}>
                <Typography
                  variant="h4"
                  fontWeight={600}
                  sx={{ mb: 4, textAlign: "center" }}
                >
                  {t("about.section3_title")}
                </Typography>
                <Grid container spacing={3}>
                  {sections.map((step, index) => (
                    <Grid item xs={12} md={6} lg={3} key={index}>
                      <Fade in={true} timeout={500 + index * 200}>
                        <Card
                          elevation={2}
                          sx={{
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.divider}`,
                            height: "100%",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              elevation: 4,
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <CardContent sx={{ p: 3, textAlign: "center" }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: "50%",
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.1
                                  ),
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {step.icon}
                              </Box>
                            </Box>
                            <Chip
                              label={`Step ${index + 1}`}
                              color="primary"
                              size="small"
                              sx={{ mb: 2 }}
                            />
                            <Typography
                              variant="h6"
                              fontWeight={600}
                              sx={{ mb: 2 }}
                            >
                              {step.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ lineHeight: 1.6 }}
                            >
                              {step.description}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ mt: 4, textAlign: "center" }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ maxWidth: 800, mx: "auto", lineHeight: 1.8 }}
                  >
                    {t("about.section3_summary")}
                  </Typography>
                </Box>
              </Box>

              {/* Features Section */}
              <Box sx={{ mb: 8 }}>
                <Typography
                  variant="h4"
                  fontWeight={600}
                  sx={{ mb: 4, textAlign: "center" }}
                >
                  Platform Features
                </Typography>
                <Grid container spacing={3}>
                  {features.map((feature, index) => (
                    <Grid item xs={12} md={4} key={index}>
                      <Fade in={true} timeout={600 + index * 200}>
                        <Card
                          elevation={1}
                          sx={{
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.divider}`,
                            height: "100%",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              elevation: 3,
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <CardContent sx={{ p: 3, textAlign: "center" }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 50,
                                  height: 50,
                                  borderRadius: "50%",
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.1
                                  ),
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {feature.icon}
                              </Box>
                            </Box>
                            <Typography
                              variant="h6"
                              fontWeight={600}
                              sx={{ mb: 2 }}
                            >
                              {feature.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ lineHeight: 1.6 }}
                            >
                              {feature.description}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* FAQ Section */}
              <Card
                elevation={2}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h4"
                    fontWeight={600}
                    sx={{ mb: 4, textAlign: "center" }}
                  >
                    Frequently Asked Questions
                  </Typography>
                  <FAQ />
                </CardContent>
              </Card>
            </Box>
          </Fade>
        </Box>
        <Footer />
      </NavPage>
    </Box>
  );
}

export default AboutUsPage;
